import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Hourglass, Plus } from "lucide-react";
import { formatDuration, parseDurationToSeconds } from "@/lib/format";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function TimeInline({ task, canEdit, onLogged }) {
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sec = parseDurationToSeconds(duration);
    if (!sec) { toast.error("Enter a duration like '1h 30m'"); return; }
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/time-logs`, { duration_seconds: sec, note });
      toast.success(`+${formatDuration(sec)} logged`);
      setDuration(""); setNote("");
      setOpen(false);
      onLogged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log time");
    } finally {
      setBusy(false);
    }
  };

  const trigger = (
    <button
      disabled={!canEdit}
      onClick={(e) => e.stopPropagation()}
      className="font-mono-data text-xs text-neutral-700 hover:text-neutral-950 flex items-center justify-end gap-1 transition-colors"
      data-testid={`inline-time-${task.id}`}
    >
      {(task.total_time_seconds || 0) > 0 ? (
        <><Hourglass className="w-3 h-3 text-neutral-400" />{formatDuration(task.total_time_seconds)}</>
      ) : (
        <span className="text-neutral-300 flex items-center gap-1"><Hourglass className="w-3.5 h-3.5" />{canEdit ? "Log" : "—"}</span>
      )}
    </button>
  );

  if (!canEdit) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500">Log time</div>
          <div className="font-mono-data text-xs text-neutral-500">Total: {formatDuration(task.total_time_seconds || 0)}</div>
        </div>
        <form onSubmit={submit} className="space-y-2">
          <Input
            autoFocus
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g. 1h 30m, 45m, 30"
            className="h-9 rounded-sm"
            data-testid={`inline-time-duration-${task.id}`}
          />
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="h-9 rounded-sm"
            data-testid={`inline-time-note-${task.id}`}
          />
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-9 rounded-sm bg-neutral-950 hover:bg-neutral-800"
            data-testid={`inline-time-submit-${task.id}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />{busy ? "Saving…" : "Log time"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
